import {
    CreateButton,
    DataTable,
    DateField,
    ExportButton,
    List,
    ReferenceField,
    SearchInput,
    SelectInput,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { AlertCircle, Calendar, CheckCircle2 } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { useThreeWayMatch } from '../documents/invoices/useThreeWayMatch';
import { TopToolbar } from '../layout/TopToolbar';

const StatusCell = () => {
    const record = useRecordContext();
    if (!record) return null;
    return <StatusBadge status={record.status} />;
};

const AmountCell = () => {
    const record = useRecordContext();
    if (!record?.total_amount) return <span className="text-muted-foreground text-xs">—</span>;
    const outstanding = (record.total_amount || 0) - (record.amount_paid || 0);
    return (
        <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1 font-bold text-destructive">
                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(record.total_amount)}
            </div>
            {outstanding > 0.5 && (
                <span className="text-[10px] font-semibold text-warning">
                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(outstanding)} outstanding
                </span>
            )}
        </div>
    );
};

const MatchCell = () => {
    const record = useRecordContext();
    const { loading, applicable, fullyMatched } = useThreeWayMatch(record);
    if (!record) return null;
    if (!applicable) {
        return (
            <Badge variant="outline" className="text-[10px] font-bold gap-1 bg-muted text-muted-foreground border-border">
                <AlertCircle className="w-3 h-3" /> No Match
            </Badge>
        );
    }
    if (loading) return <span className="text-muted-foreground text-xs">…</span>;
    return (
        <Badge variant="outline" className={`text-[10px] font-bold gap-1 ${
            fullyMatched ? 'bg-success/10 text-success border-success/30' : 'bg-destructive/10 text-destructive border-destructive/30'
        }`}>
            {fullyMatched ? <><CheckCircle2 className="w-3 h-3" /> 3WM ✓</> : <><AlertCircle className="w-3 h-3" /> Mismatch</>}
        </Badge>
    );
};

const VendorBillListActions = () => (
    <TopToolbar>
        <ExportButton />
        <CreateButton label="New Bill" />
    </TopToolbar>
);

const filters = [
    <SearchInput key="q" source="q" alwaysOn />,
    <SelectInput key="status" source="status" choices={[
        { id: 'draft',            name: 'Draft' },
        { id: 'pending_approval', name: 'Pending Approval' },
        { id: 'approved',         name: 'Approved' },
        { id: 'paid',             name: 'Paid' },
        { id: 'overdue',          name: 'Overdue' },
        { id: 'cancelled',        name: 'Cancelled' },
    ]} />,
];

export const VendorBillList = () => (
    <List
        title="Vendor Bills"
        actions={<VendorBillListActions />}
        filters={filters}
        sort={{ field: 'bill_date', order: 'DESC' }}
        className="p-6 bg-muted/50"
    >
        <Card className="border-none shadow-sm overflow-hidden bg-card">
            <DataTable rowClick="show">
                <DataTable.Col
                    source="reference"
                    label="Bill No."
                    className="font-mono font-bold text-foreground"
                />
                <DataTable.Col label="Vendor">
                    <ReferenceField source="vendor_id" reference="vendors" link={false}>
                        <TextField source="name" className="font-semibold text-info" />
                    </ReferenceField>
                </DataTable.Col>
                <DataTable.Col label="Date">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                        <Calendar className="w-3 h-3" />
                        <DateField source="bill_date" />
                    </div>
                </DataTable.Col>
                <DataTable.Col label="Due Date">
                    <div className="flex items-center gap-2 text-warning text-xs font-semibold">
                        <Calendar className="w-3 h-3" />
                        <DateField source="due_date" />
                    </div>
                </DataTable.Col>
                <DataTable.Col label="3-Way Match">
                    <MatchCell />
                </DataTable.Col>
                <DataTable.Col label="Status">
                    <StatusCell />
                </DataTable.Col>
                <DataTable.Col label="Amount">
                    <AmountCell />
                </DataTable.Col>
            </DataTable>
        </Card>
    </List>
);
